import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { connection } from '../api/connection';
import { useApi } from '../api/context';
import { useAuth, usePermission } from '../auth/context';
import { DockProvider } from '../dock/DockProvider';
import { Workspace } from '../dock/Workspace';
import { usePhone } from '../shell/breakpoints';
import { CommandPalette } from '../shell/CommandPalette';
import { PhoneAppBar, PhoneTabBar } from '../shell/PhoneBars';
import { Rail } from '../shell/Rail';
import { ShellContext, type ShellActions } from '../shell/shellContext';
import { focusListSearch, focusRegion, isTypingTarget, modalOpen } from '../shell/shortcuts';
import { ShortcutsDialog } from '../shell/ShortcutsDialog';
import { StatusBar } from '../shell/StatusBar';
import { ThemeProvider } from '../shell/ThemeProvider';
import { TopBar } from '../shell/TopBar';
import { strings } from '../strings';

/** How long the second key of a `g` sequence may take (spec 6.1.12). */
const SEQUENCE_MS = 1500;

/** The destinations of `g` sequences (spec 6.1.12); `g n` only while networking is on (feature spec 17). */
const GO_TO: Partial<Record<string, string>> = {
  b: '/blueprints',
  d: '/deployments',
  n: '/networks',
  r: '/runs',
  c: '/cloud-accounts',
};

/** The global shortcuts of spec 6.1.12. */
function useShellShortcuts(actions: ShellActions) {
  const navigate = useNavigate();
  const canOperate = usePermission('operate');
  const networking = useAuth().config.networking?.enabled ?? false;
  const pendingG = useRef(0);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || modalOpen()) return;
      const ctrl = event.ctrlKey || event.metaKey;
      if (ctrl && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        actions.openPalette();
        return;
      }
      if (event.key === 'F6' && !ctrl && !event.altKey) {
        event.preventDefault();
        focusRegion(event.shiftKey ? -1 : 1);
        return;
      }
      if (ctrl || event.altKey || isTypingTarget(event.target)) return;
      if (event.key === '?') {
        event.preventDefault();
        actions.openShortcuts();
        return;
      }
      if (event.key === '/' && focusListSearch()) {
        event.preventDefault();
        return;
      }
      const key = event.key.toLowerCase();
      const target = key === 'n' && !networking ? undefined : GO_TO[key];
      if (Date.now() - pendingG.current < SEQUENCE_MS && target) {
        pendingG.current = 0;
        if (target !== '/cloud-accounts' || canOperate) navigate(target);
        event.preventDefault();
        return;
      }
      pendingG.current = key === 'g' && !event.shiftKey ? Date.now() : 0;
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [actions, navigate, canOperate, networking]);
}

/** While the API is unreachable, `GET /config` is retried every 5 seconds (spec 6.1.8). */
function useConnectionProbe() {
  const api = useApi();
  useEffect(() => {
    const unregister = connection.setProbe(() => api.getConfig());
    const onOffline = () => connection.requestFailed();
    const onOnline = () => void api.getConfig().catch(() => {});
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      unregister();
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [api]);
}

function Shell() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [pageStatus, setPageStatus] = useState<string | null>(null);

  const actions = useMemo<ShellActions>(
    () => ({
      openPalette: () => setPaletteOpen(true),
      openShortcuts: () => setShortcutsOpen(true),
      focusNextRegion: () => focusRegion(1),
      setPageStatus,
    }),
    [],
  );
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const openShortcuts = useCallback(() => setShortcutsOpen(true), []);
  useShellShortcuts(actions);
  useConnectionProbe();
  // Below 768 px the phone layout replaces the top bar, rail and status bar (spec 6.12).
  const phone = usePhone();

  return (
    <ShellContext.Provider value={actions}>
      <a
        href="#main"
        className="skip-link"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById('main')?.focus();
        }}
      >
        {strings.shell.skipToMain}
      </a>
      {/* The workspace keeps its place in the tree, so the page stays mounted when the layout switches. */}
      <div className={phone ? 'shell is-phone' : 'shell'}>
        {phone ? <PhoneAppBar /> : <TopBar />}
        {phone ? null : <Rail />}
        <Workspace>
          <Outlet />
        </Workspace>
        {phone ? <PhoneTabBar /> : <StatusBar pageStatus={pageStatus} />}
      </div>
      {paletteOpen && <CommandPalette onClose={closePalette} openShortcuts={openShortcuts} />}
      {shortcutsOpen && <ShortcutsDialog onClose={() => setShortcutsOpen(false)} />}
    </ShellContext.Provider>
  );
}

/** The application shell of spec 6.1: top bar, rail, page and status bar, with theme and shortcuts. */
export function AppLayout() {
  const { me } = useAuth();
  const user = me.id ?? me.name;
  return (
    <ThemeProvider key={user} user={user}>
      <DockProvider user={user}>
        <Shell />
      </DockProvider>
    </ThemeProvider>
  );
}

/** Padding and width for pages other than the designer, which uses the full main area (spec 6.1.3). */
/** Pages sit in a centered container at most 1240 px wide; `full` (deployment and run pages) uses the full width. */
export function PageContainer({ children, full }: { children: ReactNode; full?: boolean }) {
  return <div className={`mx-auto px-6 py-5 ${full ? 'page-full' : 'max-w-[1240px]'}`}>{children}</div>;
}
